import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Drawer from "@/components/admin/Drawer";
import StatusBadge, { STATUS_OPTIONS } from "@/components/admin/StatusBadge";
import { Loader2, Trash2, Eye, Search, Mail, Phone, MapPin, Bed, Building2 } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminHospitals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [statusEdit, setStatusEdit] = useState("new");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/partnerships/hospitals");
      setItems(res.data || []);
    } catch (e) { toast.error("Failed to load inquiries"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(it => {
      if (statusFilter !== "all" && (it.status || "new") !== statusFilter) return false;
      if (!q) return true;
      return [it.hospital_name, it.contact_person, it.email, it.city, it.state].filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [items, query, statusFilter]);

  const openItem = (it) => { setSelected(it); setNotes(it.notes || ""); setStatusEdit(it.status || "new"); };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/admin/partnerships/hospitals/${selected.id}`, { status: statusEdit, notes });
      toast.success("Updated");
      await load();
      setSelected(null);
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await api.delete(`/admin/partnerships/hospitals/${id}`); toast.success("Deleted"); setSelected(null); await load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="admin-hospitals">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#0F2A4A]">Hospital Inquiries</h1>
          <p className="text-slate-600 text-sm">Hospitals interested in partnering with HomeCure.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." className="hc-input pl-9 w-56" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="hc-input w-40">
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#3780E0]" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No inquiries yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#3780E0] text-white text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Hospital</th>
                  <th className="px-4 py-3 font-semibold">Contact Person</th>
                  <th className="px-4 py-3 font-semibold">City</th>
                  <th className="px-4 py-3 font-semibold">Beds</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Received</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it, idx) => (
                  <tr key={it.id} className={idx % 2 ? "bg-[#E8F0FB]" : "bg-white"}>
                    <td className="px-4 py-3 font-semibold text-[#0F2A4A]">{it.hospital_name}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{it.contact_person}</div>
                      <div className="text-xs text-slate-500">{it.designation || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{it.city}{it.state ? `, ${it.state}` : ''}</td>
                    <td className="px-4 py-3 text-slate-700">{it.bed_count ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(it.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => openItem(it)} className="p-2 rounded hover:bg-slate-100" title="View"><Eye className="w-4 h-4 text-[#3780E0]" /></button>
                        <button onClick={() => del(it.id)} className="p-2 rounded hover:bg-red-50" title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.hospital_name || "Hospital inquiry"}
        footer={
          <div className="flex justify-between gap-3 items-center">
            <button onClick={() => del(selected?.id)} className="text-red-600 text-sm font-semibold inline-flex items-center gap-1.5 hover:text-red-700"><Trash2 className="w-4 h-4" /> Delete</button>
            <button onClick={save} disabled={saving} className="hc-btn-primary disabled:opacity-60">{saving ? "Saving…" : "Save changes"}</button>
          </div>
        }
      >
        {selected && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 flex items-center gap-2 text-[#0F2A4A] font-bold"><Building2 className="w-4 h-4 text-[#3780E0]" /> {selected.hospital_name}</div>
              <div><div className="text-xs text-slate-500">Contact</div><div>{selected.contact_person}</div><div className="text-xs text-slate-500">{selected.designation || ""}</div></div>
              <div><div className="text-xs text-slate-500">Received</div><div>{formatDate(selected.created_at)}</div></div>
              <div><div className="text-xs text-slate-500">Email</div><a href={`mailto:${selected.email}`} className="hc-link"><Mail className="w-3.5 h-3.5 inline mr-1" />{selected.email}</a></div>
              <div><div className="text-xs text-slate-500">Phone</div><a href={`tel:${selected.phone}`} className="hc-link"><Phone className="w-3.5 h-3.5 inline mr-1" />{selected.phone}</a></div>
              <div><div className="text-xs text-slate-500">Location</div><div><MapPin className="w-3.5 h-3.5 inline mr-1 text-[#3780E0]" />{selected.city}{selected.state ? `, ${selected.state}` : ''}</div></div>
              <div><div className="text-xs text-slate-500">Registration No.</div><div>{selected.registration_number || <span className="text-slate-400">—</span>}</div></div>
              <div className="col-span-2"><div className="text-xs text-slate-500">Beds</div><div><Bed className="w-3.5 h-3.5 inline mr-1 text-[#3780E0]" />{selected.bed_count ?? '—'}</div></div>
            </div>
            {selected.message && (
              <div>
                <div className="text-xs text-slate-500 mb-1">Message</div>
                <div className="p-3 bg-[#E8F0FB] rounded border border-[#C3D9F6] whitespace-pre-wrap">{selected.message}</div>
              </div>
            )}
            <div>
              <label className="hc-label">Status</label>
              <select value={statusEdit} onChange={(e) => setStatusEdit(e.target.value)} className="hc-input">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="hc-label">Internal notes</label>
              <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="hc-input" />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
